'use client';
import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface ZohoSyncPanelProps {
  type: 'proposal' | 'customer';
  payload: Record<string, any>;
  isLight: boolean;
}

interface SyncState {
  status: 'idle' | 'syncing' | 'success' | 'error';
  message: string;
  zohoId?: string;
  action?: string;
  lastSynced?: string;
}

export default function ZohoSyncPanel({ type, payload, isLight }: ZohoSyncPanelProps) {
  const [sync, setSync] = useState<SyncState>({ status: 'idle', message: '' });
  const [expanded, setExpanded] = useState(false);

  const cardBg = isLight ? '#FFFFFF' : '#0B1628';
  const borderColor = isLight ? '#E2E8F0' : 'rgba(30,144,255,0.12)';
  const mutedText = isLight ? '#64748B' : '#6B7FA3';
  const headingText = isLight ? '#0F172A' : '#FFFFFF';
  const sectionBg = isLight ? '#F8FAFC' : '#0D1829';

  const endpoint =
    type === 'proposal' ? '/api/zoho/sync-proposal' : '/api/zoho/sync-customer';

  const handleSync = async () => {
    setSync({ status: 'syncing', message: 'Connecting to Zoho CRM…' });
    try {
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();

      if (!res.ok || data.error) {
        setSync({ status: 'error', message: data.error || 'Sync failed' });
        return;
      }

      setSync({
        status: 'success',
        message: data.message || 'Synced successfully',
        zohoId: data.zohoId,
        action: data.action,
        lastSynced: new Date().toLocaleTimeString(),
      });
    } catch (err: any) {
      setSync({ status: 'error', message: err.message || 'Network error' });
    }
  };

  const statusConfig = {
    idle: { color: '#6B7FA3', icon: 'CloudArrowUpIcon', label: 'Not synced' },
    syncing: { color: '#1E90FF', icon: 'ArrowPathIcon', label: 'Syncing…' },
    success: { color: '#00D68F', icon: 'CheckCircleIcon', label: 'Synced' },
    error: { color: '#FF4757', icon: 'ExclamationCircleIcon', label: 'Sync failed' },
  };

  const cfg = statusConfig[sync.status];

  const syncLabel = type === 'proposal' ? 'Sync Deal to Zoho' : 'Sync Account to Zoho';
  const syncDesc =
    type === 'proposal' ?'Push this proposal as a Deal/Opportunity in Zoho CRM with stage and amount.' :'Push this customer as an Account and Contact in Zoho CRM.';

  return (
    <div
      className="rounded-xl overflow-hidden"
      style={{ background: cardBg, border: `1px solid ${borderColor}` }}
    >
      {/* Header row */}
      <button
        className="w-full flex items-center gap-3 px-4 py-3 text-left transition-colors"
        style={{ background: expanded ? sectionBg : 'transparent' }}
        onClick={() => setExpanded((v) => !v)}
      >
        {/* Zoho logo mark */}
        <div
          className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 font-800 text-sm"
          style={{ background: 'linear-gradient(135deg, #E42527, #F5A623)', color: '#fff' }}
        >
          Z
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-700" style={{ color: headingText }}>
            Zoho CRM
          </p>
          <p className="text-[11px]" style={{ color: mutedText }}>
            {sync.status === 'success' && sync.lastSynced
              ? `Last synced at ${sync.lastSynced}`
              : syncDesc}
          </p>
        </div>
        {/* Status pill */}
        <span
          className="text-[10px] font-700 px-2 py-0.5 rounded-full shrink-0"
          style={{
            background: `${cfg.color}18`,
            color: cfg.color,
            border: `1px solid ${cfg.color}35`,
          }}
        >
          ● {cfg.label}
        </span>
        <Icon
          name={expanded ? 'ChevronUpIcon' : 'ChevronDownIcon'}
          size={14}
          style={{ color: mutedText } as React.CSSProperties}
        />
      </button>

      {/* Expanded body */}
      {expanded && (
        <div className="px-4 pb-4 pt-2 space-y-3" style={{ borderTop: `1px solid ${borderColor}` }}>
          {/* Payload preview */}
          <div className="rounded-lg p-3 space-y-1.5" style={{ background: sectionBg }}>
            <p className="text-[10px] font-700 uppercase tracking-wider mb-2" style={{ color: mutedText }}>
              Data to sync
            </p>
            {type === 'proposal' ? (
              <>
                <Row label="Deal Name" value={`${payload.code} · ${payload.title}`} headingText={headingText} mutedText={mutedText} />
                <Row label="Account" value={payload.customer} headingText={headingText} mutedText={mutedText} />
                <Row label="Amount" value={payload.value} headingText={headingText} mutedText={mutedText} />
                <Row label="Stage" value={payload.stage} headingText={headingText} mutedText={mutedText} />
                <Row label="Owner" value={payload.owner} headingText={headingText} mutedText={mutedText} />
              </>
            ) : (
              <>
                <Row label="Account Name" value={payload.name} headingText={headingText} mutedText={mutedText} />
                <Row label="Industry" value={payload.industry} headingText={headingText} mutedText={mutedText} />
                <Row label="Contact" value={payload.contact} headingText={headingText} mutedText={mutedText} />
                <Row label="Status" value={payload.status} headingText={headingText} mutedText={mutedText} />
              </>
            )}
          </div>

          {/* Result message */}
          {sync.status !== 'idle' && (
            <div
              className="flex items-start gap-2 rounded-lg px-3 py-2.5"
              style={{
                background: `${cfg.color}10`,
                border: `1px solid ${cfg.color}30`,
              }}
            >
              <Icon
                name={cfg.icon as any}
                size={14}
                className={sync.status === 'syncing' ? 'animate-spin' : ''}
                style={{ color: cfg.color, marginTop: 1 } as React.CSSProperties}
              />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-600" style={{ color: cfg.color }}>
                  {sync.message}
                </p>
                {sync.zohoId && (
                  <p className="text-[10px] mt-0.5" style={{ color: mutedText }}>
                    Zoho ID: {sync.zohoId}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Action buttons */}
          <div className="flex items-center gap-2">
            <button
              onClick={handleSync}
              disabled={sync.status === 'syncing'}
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-700 transition-all disabled:opacity-60"
              style={{
                background: 'linear-gradient(135deg, #E42527, #F5A623)',
                color: '#fff',
                boxShadow: '0 2px 8px rgba(228,37,39,0.3)',
              }}
            >
              <Icon
                name={sync.status === 'syncing' ? 'ArrowPathIcon' : 'CloudArrowUpIcon'}
                size={13}
                className={sync.status === 'syncing' ? 'animate-spin' : ''}
              />
              {sync.status === 'syncing' ? 'Syncing…' : syncLabel}
            </button>
            {sync.status !== 'idle' && (
              <button
                onClick={() => setSync({ status: 'idle', message: '' })}
                className="px-3 py-2 rounded-lg text-xs font-600 transition-all"
                style={{ background: sectionBg, color: mutedText, border: `1px solid ${borderColor}` }}
              >
                Reset
              </button>
            )}
          </div>

          {/* Credential hint */}
          {sync.status === 'error' && sync.message.includes('credentials') && (
            <p className="text-[10px] rounded-lg px-3 py-2" style={{ background: 'rgba(255,71,87,0.06)', color: '#FF4757', border: '1px solid rgba(255,71,87,0.2)' }}>
              ⚙ Set ZOHO_CLIENT_ID, ZOHO_CLIENT_SECRET, and ZOHO_REFRESH_TOKEN in your .env file to enable sync.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function Row({
  label,
  value,
  headingText,
  mutedText,
}: {
  label: string;
  value: string;
  headingText: string;
  mutedText: string;
}) {
  return (
    <div className="flex items-center justify-between gap-2">
      <span className="text-[11px]" style={{ color: mutedText }}>
        {label}
      </span>
      <span className="text-[11px] font-600 text-right truncate max-w-[60%]" style={{ color: headingText }}>
        {value}
      </span>
    </div>
  );
}
